import { useTranslate, useMany } from '@refinedev/core';
import { Card, Table, Tag, Typography, theme } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import type {
  PublicationListOut,
  RankingMovement,
  PublicationRankingMovementOut,
} from '@/api/generated/schemas';
import { ModStatus } from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { formatDateTime } from '@/utils/format';

interface PublicationRankingsCardProps {
  publicationRankings: PublicationRankingMovementOut[];
  /** Baseline of the movement values; no baseline → movement is noise. */
  movementSnapshotAt?: string | null;
}

const MovementCell = ({ movement }: { movement: RankingMovement }) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  if (movement === 'new') {
    return (
      <Tag color="blue">{t('user_catalog.rankings.show.movement_new')}</Tag>
    );
  }
  if (movement > 0) {
    return (
      <Typography.Text style={{ color: token.colorSuccess }}>
        <ArrowUpOutlined /> {movement}
      </Typography.Text>
    );
  }
  if (movement < 0) {
    return (
      <Typography.Text style={{ color: token.colorError }}>
        <ArrowDownOutlined /> {-movement}
      </Typography.Text>
    );
  }
  return <Typography.Text type="secondary">—</Typography.Text>;
};

// Read-only ranked list; titles resolved via useMany, unknown ids fall
// back to `#id`.
export const PublicationRankingsCard = ({
  publicationRankings,
  movementSnapshotAt,
}: PublicationRankingsCardProps) => {
  const t = useTranslate();

  const sorted = [...publicationRankings].sort(
    (a, b) => a.position - b.position
  );
  const publicationIds = sorted.map((rp) => rp.publication_id);

  const { result: publicationsResult, query: publicationsQuery } =
    useMany<PublicationListOut>({
      resource: 'user_catalog.publications',
      ids: publicationIds,
      queryOptions: { enabled: publicationIds.length > 0 },
    });

  const publicationById = new Map(
    publicationsResult?.data.map((p) => [p.id, p])
  );

  return (
    <Card
      title={t('user_catalog.rankings.show.publications')}
      extra={
        movementSnapshotAt && (
          <Typography.Text type="secondary">
            {t('user_catalog.rankings.show.movement_snapshot', {
              date: formatDateTime(movementSnapshotAt),
            })}
          </Typography.Text>
        )
      }
    >
      <Table<PublicationRankingMovementOut>
        dataSource={sorted}
        rowKey="id"
        pagination={false}
        size="small"
        locale={{
          emptyText: t('user_catalog.rankings.show.publications_empty'),
        }}
      >
        <Table.Column
          dataIndex="position"
          title={t('user_catalog.rankings.fields.position')}
          width={80}
        />
        <Table.Column
          dataIndex="movement"
          title={t('user_catalog.rankings.fields.movement')}
          width={100}
          render={(movement: RankingMovement) =>
            movementSnapshotAt ? <MovementCell movement={movement} /> : '—'
          }
        />
        <Table.Column
          dataIndex="publication_id"
          title={t('user_catalog.rankings.fields.publication')}
          render={(id: number) => {
            const publication = publicationById.get(id);
            return (
              <>
                <PublicationInfoPopover
                  publication={publication}
                  // Per-row fallback fetch only once the batch lookup settled
                  // without this id — avoids N parallel requests while loading.
                  publicationId={
                    publication || publicationsQuery.isLoading ? undefined : id
                  }
                >
                  {publication?.title ?? `#${id}`}
                </PublicationInfoPopover>
                {/* Flag only trouble; enabled is the unremarkable default. */}
                {publication &&
                  publication.mod_status !== ModStatus.enabled && (
                    <span style={{ marginInlineStart: 8 }}>
                      <ModStatusTag value={publication.mod_status} />
                    </span>
                  )}
              </>
            );
          }}
        />
      </Table>
    </Card>
  );
};
